import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { jobPostingLd } from "@/lib/jobSchema";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import WhatsAppButton from "@/components/WhatsAppButton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Building2, MapPin, Loader2, Lock, CheckCircle2, Wallet, CalendarDays } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const mediaUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);

export default function PublicJob() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/public/jobs/${id}`).then(({ data }) => setJob(data)).catch(() => setError(true));
  }, [id]);

  useSeo(job ? {
    title: `${job.title} at ${job.company} — ${job.location} | Classroom to Corporate`,
    description: `${job.type} ${job.title} job at ${job.company}, ${job.location}${job.salary ? `, ${job.salary}` : ""}. ${job.description || ""}`.slice(0, 160),
    jsonLd: jobPostingLd(job, window.location.href),
  } : {});

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 border-b border-slate-100 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <button onClick={() => navigate("/")} className="flex items-center gap-2" data-testid="public-job-brand">
            <img src="/logo.png" alt="Classroom to Corporate" className="h-9 w-9 object-contain" />
            <span className="font-heading text-lg font-bold text-slate-900 hidden sm:inline">Classroom to <span className="text-amber-600">Corporate</span></span>
          </button>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Button onClick={() => navigate("/login")} className="bg-amber-600 hover:bg-amber-700" data-testid="public-job-signin">{t("browse.signIn")}</Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 sm:px-6 py-8" data-testid="public-job-page">
        <button onClick={() => navigate("/browse?tab=jobs")} className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900" data-testid="public-job-back">
          <ArrowLeft className="h-4 w-4" /> {t("publicJob.back")}
        </button>
        {error ? (
          <p className="py-16 text-center text-slate-500" data-testid="public-job-error">{t("publicJob.notFound")}</p>
        ) : !job ? (
          <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-amber-600" /></div>
        ) : (
          <JobArticle job={job} t={t} onApply={() => navigate("/login")} />
        )}
      </main>
      <WhatsAppButton />
    </div>
  );
}

const JobArticle = ({ job, t, onApply }) => (
  <article className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-10 shadow-sm">
    <div className="flex items-start gap-4">
      {job.company_logo && <img src={mediaUrl(job.company_logo)} alt={`${job.company} logo`} className="h-16 w-16 shrink-0 rounded-xl border border-slate-200 object-contain bg-white" data-testid="public-job-logo" />}
      <div className="min-w-0">
        <h1 className="font-heading text-3xl font-extrabold text-slate-900 sm:text-4xl" data-testid="public-job-title">{job.title}</h1>
        <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-slate-600">
          <span className="inline-flex items-center gap-1.5"><Building2 className="h-4 w-4" /> {job.company}</span>
          <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {job.location}</span>
        </p>
      </div>
    </div>
    <div className="mt-5 flex flex-wrap gap-2">
      <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">{job.type}</Badge>
      {job.salary && <Badge variant="outline" className="gap-1" data-testid="public-job-salary"><Wallet className="h-3.5 w-3.5" /> {job.salary}</Badge>}
      {job.created_at && <Badge variant="outline" className="gap-1"><CalendarDays className="h-3.5 w-3.5" /> {t("publicJob.posted")} {new Date(job.created_at).toLocaleDateString()}</Badge>}
    </div>
    <h2 className="mt-8 font-heading text-lg font-bold text-slate-900">{t("publicJob.about")}</h2>
    <p className="mt-2 leading-relaxed text-slate-700" data-testid="public-job-description">{job.description}</p>
    {job.requirements?.length > 0 && (
      <>
        <h2 className="mt-8 font-heading text-lg font-bold text-slate-900">{t("publicJob.requirements")}</h2>
        <ul className="mt-3 space-y-2" data-testid="public-job-requirements">
          {job.requirements.map((r) => (
            <li key={r} className="flex items-start gap-2 text-slate-700"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {r}</li>
          ))}
        </ul>
      </>
    )}
    <Button onClick={onApply} size="lg" className="mt-10 h-12 w-full bg-amber-600 hover:bg-amber-700 text-base font-semibold sm:w-auto sm:px-10" data-testid="public-job-apply">
      <Lock className="mr-2 h-4 w-4" /> {t("browse.signInToApply")}
    </Button>
  </article>
);
